import {
  ThemeBreakpointsSchema,
  ThemeButtonVariantsSchema,
  ThemeColorKey,
  ThemeColorsSchema,
  ThemeFontFamilyItemSchema,
  ThemeFontFamilySchema,
  ThemeProps,
  ThemeShadowSchema,
  ThemeTextVariantsSchema,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import {
  FONT_WEIGHT_MAP,
  resolveTailwindBreakpoints,
  TAILWIND_BREAKPOINTS,
  type TailwindBreakpoint,
} from '@mutiny-pkg/tailwind-parser';
import { LexicalThemeColors } from '../../../richtext/types';
import { parameterize } from '@mutiny-pkg/formatters';
import z from 'zod';

export const TAILWIND_CSS_CLASS = 'mutiny-tw';
type FlexiblePageThemeVariableName =
  | `--color-${string}`
  | `--font-${string}`
  | `--shadow-${'sm' | 'md' | 'lg' | 'xl' | '2xl'}`
  | `--breakpoint-${TailwindBreakpoint}`;

export type FontFaceType = {
  fontFamily: string;
  fontWeight: string;
  src: string;
  fontStyle: string;
};

// A canvas iframe owns its font registrations; parent-page loads cannot satisfy them.
type FontRegistration = {
  element: HTMLStyleElement;
  fontFace?: FontFace;
  loaded?: Promise<void>;
};
const fontLoadsByDocument = new WeakMap<Document, Map<string, FontRegistration>>();

const FONT_LOAD_TIMEOUT_MS = 3000;
const FONT_LOAD_RETRY_DELAY_MS = 250;

async function loadFontWithTimeout(fontFace: FontFace) {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      fontFace.load(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(() => reject(new Error('Font load timed out')), FONT_LOAD_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timeout);
  }
}

const escapeCssString = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
const ABSOLUTE_FONT_WEIGHT = String.raw`(?:[1-9]\d{0,2}|1000)`;
const FONT_WEIGHT_PATTERN = new RegExp(`^(?:normal|bold|${ABSOLUTE_FONT_WEIGHT}(?:\\s+${ABSOLUTE_FONT_WEIGHT})?)$`);
const FONT_STYLE_PATTERN = /^(?:normal|italic|oblique(?:\s+-?(?:\d+(?:\.\d+)?|\.\d+)(?:deg|grad|rad|turn)){0,2})$/;

const hasSafeFontDescriptors = (fontWeight: string, fontStyle: string) =>
  FONT_WEIGHT_PATTERN.test(fontWeight) && FONT_STYLE_PATTERN.test(fontStyle);

export const getFontFamilies = (themeProps: ThemeProps) => {
  const fonts: z.infer<typeof ThemeFontFamilyItemSchema>[] = [];
  const fontFamilyTheme = ThemeFontFamilySchema.strip().parse(themeProps);
  fontFamilyTheme.fonts.forEach((font) => {
    if (font.family) {
      fonts.push(font);
    }
  });
  return fonts;
};

export const loadFontFace = async (fontFamily: string, fontWeight: string, src: string, style?: string) => {
  if (!fontFamily || !fontWeight || !src) return;

  const fontStyle = style || 'normal';
  if (!hasSafeFontDescriptors(fontWeight, fontStyle)) return;
  try {
    new URL(src, window.location.href);
  } catch {
    return;
  }

  const targetDocument = document;
  let fontLoads = fontLoadsByDocument.get(targetDocument);
  if (!fontLoads) {
    fontLoads = new Map();
    fontLoadsByDocument.set(targetDocument, fontLoads);
  }
  const key = JSON.stringify([fontFamily, fontWeight, fontStyle, src]);
  const fontFaceRule = `@font-face{font-family:"${escapeCssString(
    fontFamily,
  )}";font-weight:${fontWeight};font-style:${fontStyle};font-display:swap;src:url("${escapeCssString(src)}");}`;
  let cached = fontLoads.get(key);
  if (!cached) {
    const element = targetDocument.createElement('style');
    element.setAttribute('data-mtn-font', '');
    element.textContent = fontFaceRule;
    cached = { element };
    fontLoads.set(key, cached);
  }
  const registration = cached;
  if (!registration.element.isConnected) targetDocument.head.appendChild(registration.element);
  if (registration.fontFace && !targetDocument.fonts.has(registration.fontFace)) {
    targetDocument.fonts.add(registration.fontFace);
  }
  registration.loaded ??= (async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const fontFace = new FontFace(fontFamily, `url("${escapeCssString(src)}")`, {
          weight: fontWeight,
          style: fontStyle,
          display: 'swap',
        });
        registration.fontFace = fontFace;
        targetDocument.fonts.add(fontFace);
        await loadFontWithTimeout(fontFace);
        return;
      } catch (error) {
        if (registration.fontFace) targetDocument.fonts.delete(registration.fontFace);
        registration.fontFace = undefined;
        if (attempt === 0) {
          await new Promise<void>((resolve) => setTimeout(resolve, FONT_LOAD_RETRY_DELAY_MS));
        } else {
          // Preserve the CSS rule for rendering/replay, but allow a later caller to retry.
          registration.loaded = undefined;
          console.warn('Error loading font face', error);
        }
      }
    }
  })();
  return registration.loaded;
};

function srcIncludesHostname(src: string) {
  try {
    return new URL(src).hostname !== '';
  } catch {
    return false;
  }
}

export const getFontSrc = (src: string, staticAssetsBaseURL: string | null | undefined): string => {
  if (staticAssetsBaseURL && !srcIncludesHostname(src)) {
    return `https://${staticAssetsBaseURL}${src}`;
  }
  return src;
};

// Process theme font families into loadable font faces
export const processFontFamily = (
  fontFamilies?: z.infer<typeof ThemeFontFamilyItemSchema>[],
  staticAssetsBaseURL?: string | null,
): FontFaceType[] => {
  if (!fontFamilies || !Array.isArray(fontFamilies)) return [];

  const fontFaces: FontFaceType[] = [];

  fontFamilies.forEach((family) => {
    family.variants?.forEach((variant) => {
      if (variant.url) {
        fontFaces.push({
          fontFamily: family.family,
          fontWeight: FONT_WEIGHT_MAP[variant.weight] || variant.weight,
          src: getFontSrc(variant.url, staticAssetsBaseURL),
          fontStyle: variant.style,
        });
      }
    });
  });

  return fontFaces;
};

// Generate Tailwind CSS variable overrides from flattened theme keys
export const generateTailwindVariables = (
  theme: ThemeProps,
): Partial<Record<FlexiblePageThemeVariableName, string>> => {
  const variables: Partial<Record<FlexiblePageThemeVariableName, string>> = {};

  // Font families
  const fontFamilyTheme = ThemeFontFamilySchema.strip().parse(theme);
  (Object.keys(fontFamilyTheme) as Array<keyof typeof fontFamilyTheme>).forEach((key) => {
    fontFamilyTheme[key].forEach((font) => {
      if (font.family) {
        variables[`--font-${font.name}` as FlexiblePageThemeVariableName] = font.family;
      }
    });
  });

  // Colors - always emit core color tokens from schema defaults, plus any additional colors.
  const baseColorsTheme = ThemeColorsSchema.strip().parse(theme);
  variables['--color-primary'] = baseColorsTheme['colors.primary'].color;
  variables['--color-secondary'] = baseColorsTheme['colors.secondary'].color;
  variables['--color-tertiary'] = baseColorsTheme['colors.tertiary'].color;
  variables['--color-dark'] = baseColorsTheme['colors.dark'].color;
  variables['--color-light'] = baseColorsTheme['colors.light'].color;
  baseColorsTheme['colors.additional'].forEach(({ name, color }) => {
    const identifier = parameterize(name);
    variables[`--color-${identifier}` as FlexiblePageThemeVariableName] = color;
  });

  // Shadows
  const shadowTheme = ThemeShadowSchema.strip().parse(theme);
  (Object.keys(shadowTheme) as Array<keyof typeof shadowTheme>).forEach((key) => {
    const value = shadowTheme[key];
    const size = key.replace('shadow.', '');
    if (value) {
      variables[`--shadow-${size}` as FlexiblePageThemeVariableName] = value;
    }
  });

  // Breakpoints
  const breakpointsTheme = resolveTailwindBreakpoints(ThemeBreakpointsSchema.partial().parse(theme));
  for (const size of TAILWIND_BREAKPOINTS) {
    variables[`--breakpoint-${size}`] = breakpointsTheme[size];
  }

  return variables;
};

export const excludeBreakpointVariables = (
  themeVariables: Partial<Record<FlexiblePageThemeVariableName, string>>,
): Partial<Record<Exclude<FlexiblePageThemeVariableName, `--breakpoint-${string}`>, string>> => {
  return Object.fromEntries(
    Object.entries(themeVariables).filter(([variableName]) => !variableName.startsWith('--breakpoint-')),
  ) as Partial<Record<Exclude<FlexiblePageThemeVariableName, `--breakpoint-${string}`>, string>>;
};

export const generateInlineThemeVariables = (
  themeVariables: Partial<Record<FlexiblePageThemeVariableName, string>>,
): string[] => {
  // FlexiblePages' @theme inline claims emitted token names document-wide, while values are scoped to [data-flexible-page-root]; portaled canvas chrome must avoid page-theme-owned color/shadow utilities.
  const scopedThemeVariables = excludeBreakpointVariables(themeVariables);
  const scopedVariableEntries = Object.keys(scopedThemeVariables).map((key) => `${key}: var(${key});`);
  const breakpointEntries = Object.entries(themeVariables).flatMap(([key, value]) => {
    if (!key.startsWith('--breakpoint-') || !value) {
      return [];
    }
    return `${key}: ${value};`;
  });

  return [...scopedVariableEntries, ...breakpointEntries];
};

// Literal 'none' (a CSS keyword, not a Tailwind utility) reaches @apply through variant
// data and fails the whole theme block: map the properties with utility equivalents, drop
// the rest.
const NONE_UTILITY_MAP: Record<string, string> = {
  textDecoration: 'no-underline',
  textTransform: 'normal-case',
  borderRadius: 'rounded-none',
  boxShadow: 'shadow-none',
  border: 'border-none',
};

export const sanitizeVariantUtility = (property: string, value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (trimmed.toLowerCase() !== 'none') return trimmed;
  return NONE_UTILITY_MAP[property];
};

// Generate @layer components CSS for all variant definitions
export const generateVariantComponents = (theme: ThemeProps): string => {
  const components: string[] = [`.${TAILWIND_CSS_CLASS} { @apply text-dark; }`];

  // Process button variants
  const buttonVariantsTheme = ThemeButtonVariantsSchema.strip().parse(theme);
  (Object.keys(buttonVariantsTheme) as Array<keyof typeof buttonVariantsTheme>).forEach((key) => {
    const variantName = key.replace('variants.button.', '');
    const variant = buttonVariantsTheme[key];

    if (variant) {
      // Generate base class
      if (variant.base) {
        const baseUtilities = ['items-center justify-center'];
        const utilities = [
          ...baseUtilities,
          ...Object.entries(variant.base)
            .map(([property, value]) => sanitizeVariantUtility(property, value))
            .filter((utility): utility is string => utility !== undefined),
        ];
        if (utilities.length) {
          components.push(`.${TAILWIND_CSS_CLASS} .btn-${variantName} { @apply ${utilities.join(' ')} }`);
        }
      }

      // Generate hover class
      if (variant.hover) {
        const utilities = Object.entries(variant.hover)
          .map(([property, value]) => sanitizeVariantUtility(property, value))
          .filter((utility): utility is string => utility !== undefined);
        if (utilities.length) {
          components.push(`.${TAILWIND_CSS_CLASS} .btn-${variantName}:hover { @apply ${utilities.join(' ')} }`);
        }
      }
    }
  });

  // Process text variants
  const textVariantsTheme = ThemeTextVariantsSchema.strip().parse(theme);
  (Object.keys(textVariantsTheme) as Array<keyof typeof textVariantsTheme>).forEach((key) => {
    const variantName = key.replace('variants.text.', '');
    const variant = textVariantsTheme[key];

    if (variant) {
      // Generate base class with all non-hover properties
      const utilities = Object.entries(variant)
        .map(([property, value]) => sanitizeVariantUtility(property, value))
        .filter((utility): utility is string => utility !== undefined);
      if (utilities.length) {
        components.push(`.${TAILWIND_CSS_CLASS} .text-${variantName} { @apply ${utilities.join(' ')} }`);
      }
    }
  });

  return `@layer components {\n    ${components.join('\n    ')}\n  }`;
};

export type ThemeColor = {
  label: string;
  value: string;
  colorValue: string;
  tokenName?: string;
};

export const getColorsFromTheme = (theme: ThemeProps): ThemeColor[] => {
  const colorsTheme = ThemeColorsSchema.strip().parse(theme);
  const colors: ThemeColor[] = [];

  // Keys that should be excluded from the output if not present in the original theme
  const optionalColorKeys = new Set([ThemeColorKey.Secondary, ThemeColorKey.Tertiary]);

  (Object.keys(colorsTheme) as Array<keyof typeof colorsTheme>).forEach((key) => {
    if (key === 'colors.additional') {
      colorsTheme['colors.additional'].forEach(({ name, color }) => {
        const identifier = parameterize(name);
        colors.push({
          label: name,
          value: identifier,
          colorValue: color,
        });
      });
    } else {
      // Skip optional colors that don't exist in the original theme
      if (optionalColorKeys.has(key as ThemeColorKey) && theme[key] === undefined) {
        return;
      }
      const colorKey = key.replace('colors.', '');
      colors.push({
        label: colorsTheme[key].name,
        value: colorKey,
        colorValue: colorsTheme[key].color,
        tokenName: colorKey,
      });
    }
  });
  return colors;
};

export const getLexicalThemeColors = (theme: ThemeProps): LexicalThemeColors => {
  const colors = getColorsFromTheme(theme);

  // Support both old schema (textDark/textLight) and new schema (dark/light)
  // Try new schema first, fall back to old schema for backwards compatibility
  const textDarkColor =
    colors.find((c) => c.tokenName === 'dark')?.colorValue ??
    colors.find((c) => c.tokenName === 'textDark')?.colorValue ??
    '';

  const textLightColor =
    colors.find((c) => c.tokenName === 'light')?.colorValue ??
    colors.find((c) => c.tokenName === 'textLight')?.colorValue ??
    '';

  return {
    color: {
      primary: colors.find((c) => c.tokenName === 'primary')?.colorValue ?? '',
      secondary: colors.find((c) => c.tokenName === 'secondary')?.colorValue ?? '',
      tertiary: colors.find((c) => c.tokenName === 'tertiary')?.colorValue ?? '',
      default: colors.find((c) => c.tokenName === 'default')?.colorValue ?? '',
      textDark: textDarkColor,
      textLight: textLightColor,
      none: '',
      unset: '',
    },
    additionalColors: colors.filter((c) => c.tokenName === undefined).map((c) => c.colorValue),
  };
};
